<template>
  <main
    v-show="showNoIcon"
    role="main"
  >
    <div class="container padding-y-4xl">
      <article class="card roomy">
        <font-awesome-icon
          :icon="$v6.duotone.faFaceThinking"
          size="4x"
          style="--fa-secondary-opacity: 1; --fa-secondary-color: var(--fa-yellow)"
        />
        <h1 class="margin-y-2xs">An icon named &ldquo;{{ iconName }}&rdquo;?!</h1>

        <p class="size-lg line-length-lg margin-auto">
          We don't have that in
          <site-link
            :to="$search()"
            @click.native="$posthog.capture('icon_search_link_click', { context: 'icon_not_found_view' })"
            >our collection of {{ $counts.icons.all.formatted }} icons</site-link
          >. You may have typed the address (URL) incorrectly &ndash; check to make sure you've got the exact spelling.
        </p>

        <input-term
          class="line-length-md padding-y-lg margin-auto"
          :term="term"
          :is-showing-go-button="!!term"
          :label="`Search the v${$releases.active.versionParts.major} Icons`"
          @input="(newTerm) => (term = newTerm)"
          @go="(term) => $router.push($search({ term }))"
        />
      </article>

      <section
        id="options"
        class="row roomy align-stretch margin-y-4xl"
      >
        <div class="column-12 margin-bottom-2xl tablet:column-6 laptop:margin-bottom-0 laptop:column-3">
          <article
            class="card roomy red"
            style="height: 100%"
          >
            <div class="header">
              <wa-question-mark-block
                block-size="4x"
                block-color="var(--text-color)"
              />
            </div>
            <div class="section text-left padding-bottom-0">
              <h2 class="h5">Did We Do That???</h2>
              <p class="laptop:size-sm">
                Think you followed a broken link or &ldquo;{{ iconName }}&rdquo; existed previously but disappeared?
                <a
                  href="mailto:hello@fontawesome.com?subject=I found a broken link or missing icon, Font Awesome"
                  @click="supportLinkClicked('icon_not_found_view')"
                  >Let us know</a
                >.
              </p>
            </div>
          </article>
        </div>
        <div class="column-12 margin-bottom-2xl tablet:column-6 laptop:margin-bottom-0 laptop:column-3">
          <site-link
            :to="$docs('/web/add-icons/icon-wizard')"
            class="card roomy yellow"
            style="height: 100%"
            @click.native="$posthog.capture('icons_wizard_link_click', { context: 'icon_not_found_view' })"
          >
            <div class="header">
              <font-awesome-icon
                :icon="$v6.solid.faSquareDashedCirclePlus"
                fixed-width
                size="4x"
              />
            </div>
            <div class="section text-left padding-bottom-0">
              <h2 class="h5">Use the Icon Wizard</h2>
              <p class="laptop:size-sm">You can add a modifier to any icon with our Icon Wizard. Give it a try!</p>
            </div>
          </site-link>
        </div>
        <div class="column-12 margin-bottom-2xl tablet:column-6 laptop:margin-bottom-0 laptop:column-3">
          <a
            href="https://fontawesome.com/leaderboard/new#request"
            class="card roomy blue"
            style="height: 100%"
            @click="$posthog.capture('icon_requests_leaderboard_link_click', { context: 'icon_not_found_view' })"
          >
            <div class="header">
              <font-awesome-icon
                :icon="$v6.solid.faMessageQuestion"
                fixed-width
                size="4x"
              />
            </div>
            <div class="section text-left padding-bottom-0">
              <h2 class="h5">Request an Icon</h2>
              <p class="laptop:size-sm">
                Suggest {{ iconName }} to our community and vote it up the ranks of our
                <site-link :to="{ name: 'leaderboard.new' }">Icon Requests Leaderboard</site-link>
                .
              </p>
            </div>
          </a>
        </div>

        <div class="column-12 margin-bottom-2xl tablet:column-6 laptop:margin-bottom-0 laptop:column-3">
          <site-link
            :to="$docs('/web/add-icons/upload-icons')"
            class="card roomy teal"
            style="height: 100%"
            @click="$posthog.capture('docs_upload_icons_link_click', { context: 'icon_not_found_view' })"
          >
            <div class="header">
              <font-awesome-icon
                :icon="$v6.solid.faCloudArrowUp"
                fixed-width
                size="4x"
              />
            </div>
            <div class="section text-left padding-bottom-0">
              <h2 class="h5">Upload an Icon</h2>
              <p class="laptop:size-sm">Upload your own {{ iconName }} icon to a kit and easily use it just like it an official Font Awesome icon!</p>
            </div>
          </site-link>
        </div>
      </section>

      <section id="new-in-v6">
        <div class="text-center margin-bottom-2xl">
          <h2 class="h3">Might We Suggest&hellip;</h2>
          <p class="size-lg line-length-lg margin-auto">Here are some icons that do exist in Font Awesome {{ $releases.active.versionParts.major }}.</p>
        </div>

        <ais-instant-search
          :search-client="searchClient"
          :index-name="index"
        >
          <ais-configure
            :hits-per-page.camel="240"
            :distinct="true"
            :facet-filters.camel="facetFilters()"
          />

          <ais-hits>
            <div
              id="icons-listing"
              slot="default"
              slot-scope="{ items }"
              :class="`icon-listing ${resultsView}`"
            >
              <icon-link
                v-for="item in items"
                :key="item.objectID"
                :icon="item"
                :results-view="resultsView"
                :version="version"
              />
            </div>
          </ais-hits>
        </ais-instant-search>
      </section>
    </div>
  </main>
</template>

<script>
  import { createWidgetMixin } from 'vue-instantsearch'
  import { connectStats } from 'instantsearch.js/es/connectors'
  import InputTerm from 'app/views/search/components/InputTerm'
  import IconLink from '../IconLink'
  import algoliasearch from 'algoliasearch/lite'
  import { EventTrackingMixin } from 'app/mixins'

  import { fetchIndexName } from 'app/views/search/utils'

  export default {
    name: 'IconDoesNotExist',

    components: {
      InputTerm,
      IconLink
    },

    mixins: [EventTrackingMixin, createWidgetMixin({ connector: connectStats })],

    props: {
      iconName: {
        type: String
      },
      selectedStyle: {
        type: String
      },
      selectedFamily: {
        type: String
      },
      resultsView: {
        type: String,
        default: 'compact'
      },
      version: {
        type: Number,
        default: null
      }
    },

    data() {
      return {
        term: '',
        index: fetchIndexName(this.$releases.active.version),
        searchClient: algoliasearch(FONTAWESOME.ALGOLIA_APPLICATION_ID, FONTAWESOME.ALGOLIA_SEARCH_API_KEY)
      }
    },

    computed: {
      showNoIcon() {
        const nbHits = this.state?.nbHits === 0

        if (nbHits) {
          this.$posthog.capture('icon_not_found_view')
        }

        return nbHits
      }
    },

    methods: {
      facetFilters() {
        const filters = []

        filters.push(['type:icon'])

        if (this.$releases.active.versionParts.major === 6) {
          filters.push([`family:${this.selectedFamily}`])

          if (this.selectedFamily != 'brands') {
            filters.push([`style:${this.selectedStyle}`])
            filters.push(['is_new_in_v6:true'])
          }
        } else {
          filters.push([`style:${this.selectedStyle}`])
        }

        return filters
      }
    }
  }
</script>
